// ===== Imports Start =====
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FiBell, FiClock, FiX, FiCheckSquare, FiInfo,
  FiAlertTriangle, FiAlertCircle, FiCheckCircle, FiXCircle,
  FiCornerUpRight, FiCalendar, FiGitBranch, FiMail,
} from "react-icons/fi";
import axiosInstance from "../../API/interceptor";
// ===== Imports End =====

// ===== Types Start =====
interface Notification {
  id: string;
  task_id: string;
  title: string;
  message: string;
  timestamp: string;
  color: string;
  icon: string;
  is_read: boolean;
}
// ===== Types End =====

// ===== Helpers Start =====
const formatTimeAgo = (timestamp: string): string => {
  const now = new Date();
  const time = new Date(timestamp);
  const diffMs = now.getTime() - time.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  return time.toLocaleDateString();
};

const colorMap: Record<string, string> = {
  primary: "#6c5ffc",
  success: "#28a745",
  danger: "#dc3545",
  warning: "#ffc107",
  info: "#17a2b8",
  secondary: "#6c757d",
  pink: "#e83e8c",
};
const getBgColor = (color: string) => colorMap[color] || "#6c5ffc";

const feIconMap: Record<string, React.ReactNode> = {
  "fe fe-alert-triangle":  <FiAlertTriangle size={16} color="#fff" />,
  "fe fe-alert-circle":    <FiAlertCircle size={16} color="#fff" />,
  "fe fe-check-circle":    <FiCheckCircle size={16} color="#fff" />,
  "fe fe-x-circle":        <FiXCircle size={16} color="#fff" />,
  "fe fe-corner-up-right": <FiCornerUpRight size={16} color="#fff" />,
  "fe fe-calendar":        <FiCalendar size={16} color="#fff" />,
  "fe fe-git-branch":      <FiGitBranch size={16} color="#fff" />,
  "fe fe-clock":           <FiClock size={16} color="#fff" />,
  "fe fe-mail":            <FiMail size={16} color="#fff" />,
};
const getFeIcon = (icon: string): React.ReactNode =>
  feIconMap[icon] ?? <FiInfo size={16} color="#fff" />;
// ===== Helpers End =====

// ===== DropdownNotification Component Start =====
const DropdownNotification: React.FC = () => {
  // ===== State Start =====
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState<"unread" | "read">("unread");
  // ===== State End =====

  const trigger = useRef<any>(null);
  const dropdown = useRef<any>(null);

  // ===== Load on Mount + Auto Refresh Start =====
  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 120000);
    return () => clearInterval(interval);
  }, []);
  // ===== Load on Mount + Auto Refresh End =====

  // ===== Click Outside Handler Start =====
  useEffect(() => {
    const handler = ({ target }: MouseEvent) => {
      if (!dropdown.current) return;
      if (!dropdownOpen || dropdown.current.contains(target) || trigger.current.contains(target)) return;
      setDropdownOpen(false);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  });
  // ===== Click Outside Handler End =====

  // ===== ESC Key Handler Start =====
  useEffect(() => {
    const handler = ({ key }: KeyboardEvent) => {
      if (key === "Escape") { setDropdownOpen(false); setSidePanelOpen(false); }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  });
  // ===== ESC Key Handler End =====

  // ===== Load Notifications Start =====
  const loadNotifications = async () => {
    try {
      const res = await axiosInstance.get("/task_assignment/get_notifications");
      if (res.data.success) {
        setNotifications(res.data.notifications);
        setUnreadCount(res.data.unread_count);
      }
    } catch (err) {
      console.error("Error loading notifications:", err);
    }
  };
  // ===== Load Notifications End =====

  // ===== Handle Bell Click Start =====
  const handleBellClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dropdownOpen) loadNotifications();
    setDropdownOpen((o) => !o);
  };
  // ===== Handle Bell Click End =====

  // ===== Handle Notification Click Start =====
  const handleNotificationClick = async (notificationId: string, taskId: string) => {
    // Only mark-as-read here — closing the dropdown/panel is a separate,
    // explicit action (Close button, outside click, Escape), not a side
    // effect of reading one notification out of a list.
    try {
      await axiosInstance.post("/task_assignment/mark_notification_read", {
        notification_id: notificationId,
      });
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) {
      console.error("Error marking notification read:", err);
    }
    console.log("Open task:", taskId);
  };
  // ===== Handle Notification Click End =====

  // ===== Mark All Read Start =====
  const markAllRead = async () => {
    try {
      const res = await axiosInstance.post("/task_assignment/mark_all_notifications_read");
      if (res.data.success) {
        setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
        setUnreadCount(0);
      }
    } catch (err) {
      console.error("Error marking all read:", err);
    }
  };
  // ===== Mark All Read End =====

  // ===== View All Start =====
  const handleViewAll = (e: React.MouseEvent) => {
    e.preventDefault();
    setDropdownOpen(false);
    setSidePanelOpen(true);
    loadNotifications();
  };
  // ===== View All End =====

  // Unread/Read tabs — a read notification moves to the Read tab (grayed out,
  // inert) rather than disappearing from the app entirely. Each surface
  // (bell dropdown, side panel) reads off whichever tab is currently active.
  const unreadNotifications = notifications.filter((n) => !n.is_read);
  const readNotifications   = notifications.filter((n) => n.is_read);
  const activeList = activeTab === "unread" ? unreadNotifications : readNotifications;

  // ===== Render Start =====
  return (
    <>
      <div className="relative">

        {/* ===== Bell Trigger Start ===== */}
        <button
          ref={trigger}
          onClick={handleBellClick}
          className="relative bg-transparent border-none cursor-pointer px-2.5 py-1.5 flex items-center justify-center"
        >
          <FiBell size={22} className="text-[#6c757d]" />
          {unreadCount > 0 && (
            <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] bg-[#dc3545] rounded-[10px] flex items-center justify-center text-[10px] font-bold text-white px-1 leading-none border-2 border-white shadow-[0_2px_4px_rgba(0,0,0,0.2)] animate-[pulse-badge_2s_infinite] z-[1]">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
        {/* ===== Bell Trigger End ===== */}

        {/* ===== Dropdown Start ===== */}
        <div
          ref={dropdown}
          className={`absolute right-0 top-[calc(100%+8px)] w-[350px] min-w-[350px] bg-white rounded-md shadow-[0_10px_40px_rgba(0,0,0,0.15)] z-[10010] flex-col ${
            dropdownOpen ? "flex" : "hidden"
          }`}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-[#e9ecef] flex justify-between items-center">
            <h6 className="m-0 font-semibold text-[15px]">Notifications</h6>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="bg-transparent border-none text-[#6c5ffc] text-xs cursor-pointer p-0">
                Mark all read
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex border-b border-[#e9ecef]">
            <button
              onClick={() => setActiveTab("unread")}
              className={`flex-1 py-2 text-xs font-semibold bg-transparent border-none border-b-2 cursor-pointer ${
                activeTab === "unread" ? "text-[#6c5ffc] border-[#6c5ffc]" : "text-[#6c757d] border-transparent"
              }`}
            >
              Unread{unreadNotifications.length > 0 ? ` (${unreadNotifications.length})` : ""}
            </button>
            <button
              onClick={() => setActiveTab("read")}
              className={`flex-1 py-2 text-xs font-semibold bg-transparent border-none border-b-2 cursor-pointer ${
                activeTab === "read" ? "text-[#6c5ffc] border-[#6c5ffc]" : "text-[#6c757d] border-transparent"
              }`}
            >
              Read{readNotifications.length > 0 ? ` (${readNotifications.length})` : ""}
            </button>
          </div>

          {/* List */}
          <div className="max-h-[400px] overflow-y-auto overflow-x-hidden">
            {activeList.length === 0 ? (
              <div className="text-center py-8 px-4 text-[#6c757d]">
                <FiBell size={32} className="opacity-40" />
                <p className="mt-2 mb-0 text-sm">No {activeTab} notifications</p>
              </div>
            ) : (
              activeList.map((n) => (
                <div
                  key={n.id}
                  onClick={activeTab === "unread" ? () => handleNotificationClick(n.id, n.task_id) : undefined}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-[#f0f0f0] transition-colors duration-200 ${
                    activeTab === "unread"
                      ? "cursor-pointer hover:!bg-[#f8f9fa] bg-white"
                      : "cursor-default bg-[#f8f9fa] opacity-60"
                  }`}
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: getBgColor(n.color) }}
                  >
                    {getFeIcon(n.icon)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[13px] text-[#2c3e50] mb-0.5">{n.title}</div>
                    <div className="text-xs text-[#6c757d] mb-1 leading-[1.4]">{n.message}</div>
                    <div className="text-[11px] text-[#95a5a6] flex items-center gap-1">
                      <FiClock size={10} /> {formatTimeAgo(n.timestamp)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-[#e9ecef]">
            <button onClick={handleViewAll} className="w-full p-3 bg-white border-none text-[#6c5ffc] text-[13px] cursor-pointer font-medium">
              View all notifications
            </button>
          </div>
        </div>
        {/* ===== Dropdown End ===== */}

      </div>

      {/* ===== Side Panel Portal Start ===== */}
      {createPortal(
        <>
          {/* Overlay */}
          <div
            onClick={() => setSidePanelOpen(false)}
            className={`fixed inset-0 bg-black/30 z-[99999] transition-opacity duration-300 ${
              sidePanelOpen ? "opacity-100 visible" : "opacity-0 invisible"
            }`}
          />

          {/* Side Panel */}
          <div
            className={`fixed top-0 right-0 bottom-0 w-[600px] bg-white shadow-[-2px_0_10px_rgba(0,0,0,0.1)] z-[100000] flex flex-col transition-transform duration-300 ease-in-out ${
              sidePanelOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            {/* Panel Header */}
            <div className="bg-[linear-gradient(135deg,#667eea_0%,#764ba2_100%)] text-white px-[25px] py-5 shrink-0 flex items-center gap-2.5">
              <FiBell size={20} />
              <h5 className="m-0 font-semibold text-lg">All Notifications</h5>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-[#e9ecef] bg-white shrink-0">
              <button
                onClick={() => setActiveTab("unread")}
                className={`flex-1 py-3 text-sm font-semibold bg-transparent border-none border-b-2 cursor-pointer ${
                  activeTab === "unread" ? "text-[#6c5ffc] border-[#6c5ffc]" : "text-[#6c757d] border-transparent"
                }`}
              >
                Unread{unreadNotifications.length > 0 ? ` (${unreadNotifications.length})` : ""}
              </button>
              <button
                onClick={() => setActiveTab("read")}
                className={`flex-1 py-3 text-sm font-semibold bg-transparent border-none border-b-2 cursor-pointer ${
                  activeTab === "read" ? "text-[#6c5ffc] border-[#6c5ffc]" : "text-[#6c757d] border-transparent"
                }`}
              >
                Read{readNotifications.length > 0 ? ` (${readNotifications.length})` : ""}
              </button>
            </div>

            {/* Panel Body */}
            <div className="flex-1 overflow-y-auto bg-[#f8f9fa]">
              {activeList.length === 0 ? (
                <div className="text-center py-[60px] px-4 text-[#6c757d]">
                  <FiBell size={48} className="opacity-40" />
                  <p className="mt-3 text-base">No {activeTab} notifications found</p>
                </div>
              ) : (
                activeList.map((n) => (
                  <div
                    key={n.id}
                    onClick={activeTab === "unread" ? () => handleNotificationClick(n.id, n.task_id) : undefined}
                    className={`flex items-start gap-3.5 px-5 py-[15px] border-b border-[#e9ecef] transition-all duration-200 ${
                      activeTab === "unread" ? "cursor-pointer hover:-translate-x-1 bg-white" : "cursor-default bg-[#f0f1f3] opacity-60"
                    }`}
                    style={{ borderLeft: `4px solid ${getBgColor(n.color)}` }}
                  >
                    <div
                      className="w-[45px] h-[45px] rounded-full flex items-center justify-center shrink-0"
                      style={{ background: getBgColor(n.color) }}
                    >
                      {getFeIcon(n.icon)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start mb-1">
                        <h6 className="m-0 font-semibold text-sm text-[#2c3e50]">{n.title}</h6>
                        {activeTab === "unread" && (
                          <span className="bg-[#6c5ffc] text-white rounded-[10px] px-1.5 py-px text-[9px] font-bold">NEW</span>
                        )}
                      </div>
                      <p className="mt-0 mb-1 text-[13px] text-[#6c757d] leading-[1.4]">{n.message}</p>
                      <small className="text-[11px] text-[#95a5a6] flex items-center gap-1">
                        <FiClock size={10} /> {formatTimeAgo(n.timestamp)}
                      </small>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Panel Footer */}
            <div className="px-5 py-[15px] bg-white border-t-2 border-[#e9ecef] flex gap-2.5 shrink-0">
              <button
                onClick={() => setSidePanelOpen(false)}
                className="flex-1 px-5 py-2.5 border-none rounded-md bg-[#6c757d] text-white font-medium cursor-pointer flex items-center justify-center gap-2"
              >
                <FiX size={14} /> Close
              </button>
              <button
                onClick={async () => { await markAllRead(); loadNotifications(); }}
                className="flex-1 px-5 py-2.5 border-none rounded-md bg-[#667eea] text-white font-medium cursor-pointer flex items-center justify-center gap-2"
              >
                <FiCheckSquare size={14} /> Mark All as Read
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
      {/* ===== Side Panel Portal End ===== */}

      {/* ===== Pulse Badge CSS ===== */}
      <style>{`
        @keyframes pulse-badge {
          0% { box-shadow: 0 0 0 0 rgba(220,53,69,0.7); }
          70% { box-shadow: 0 0 0 8px rgba(220,53,69,0); }
          100% { box-shadow: 0 0 0 0 rgba(220,53,69,0); }
        }
      `}</style>
    </>
  );
  // ===== Render End =====
};
// ===== DropdownNotification Component End =====

export default DropdownNotification;